import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import { useAppContext } from '../context/AppContext';
import {
  FaSave,
  FaArrowLeft,
  FaUserEdit,
  FaCamera,
  FaUsers,
  FaPhone,
  FaMapMarkerAlt,
  FaCalendar,
  FaIdCard,
  FaStickyNote,
  FaUser,
} from 'react-icons/fa';
import {
  Alert,
  Spinner,
  Badge,
  Form,
  Button,
  InputGroup,
} from 'react-bootstrap';
import Select from 'react-select';

const getAuthToken = () => localStorage.getItem('authToken');

const StudentEdit = () => {
  const { id } = useParams();
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { selectedAcademicYear } = useAppContext();
  const [formData, setFormData] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedBatches, setSelectedBatches] = useState([]);

  useEffect(() => {
    fetchBatches();
    fetchStudentDetails();
  }, [selectedAcademicYear]);

  const fetchBatches = async () => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      navigate('/login');
      return;
    }

    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      setBatches([]);
      return;
    }

    try {
      const response = await axios.get(
        `${appConstants.API_ROOT_URL}get-batches-v3`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { academic_year_id: selectedAcademicYear.id },
        },
      );

      if (response.data.status === 'success') {
        setBatches(response.data.data || []);
      } else {
        setAlert({
          show: true,
          message: 'Failed to load batches.',
          variant: 'danger',
        });
      }
    } catch {
      setAlert({
        show: true,
        message: 'Error fetching batches.',
        variant: 'danger',
      });
    }
  };

  const fetchStudentDetails = async () => {
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      navigate('/login');
      return;
    }

    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      setAlert({
        show: true,
        message: 'Please select an academic year.',
        variant: 'warning',
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}get-student-v2`,
        {
          id,
          academic_year_id: selectedAcademicYear.id,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === 'success') {
        const data = response.data.data;
        setFormData(data);

        // Multi-batches from API
        if (data.batches && Array.isArray(data.batches)) {
          setSelectedBatches(data.batches.map((b) => String(b.batch_id)));
        }

        // Photo preview
        if (data.photo) {
          setPhotoPreview(
            `${appConstants.API_ROOT_URL}uploads/students/${data.photo}`,
          );
        }
      } else {
        setAlert({
          show: true,
          message: 'Failed to fetch student details.',
          variant: 'danger',
        });
        navigate(-1);
      }
    } catch {
      setAlert({
        show: true,
        message: 'Error fetching student details.',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = getAuthToken();
    if (!token) {
      setAlert({
        show: true,
        message: 'Unauthorized! Please log in.',
        variant: 'danger',
      });
      navigate('/login');
      return;
    }

    try {
      setLoading(true);
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        if (key !== 'batches') formDataToSend.append(key, formData[key] ?? '');
      });

      // Include multi-batch IDs
      selectedBatches.forEach((bid) =>
        formDataToSend.append('batch_ids[]', bid),
      );

      if (photo) {
        formDataToSend.append('photo', photo);
      } else if (formData.photo === '') {
        formDataToSend.append('delete_photo', true);
      }

      const response = await axios.post(
        `${appConstants.API_ROOT_URL}update-student-v3`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      if (response.data.status === 'success') {
        setAlert({
          show: true,
          message: 'Student updated successfully!',
          variant: 'success',
        });
        setTimeout(() => navigate(-1), 2000);
      } else {
        setAlert({
          show: true,
          message: response.data.message || 'Failed to update student.',
          variant: 'danger',
        });
      }
    } catch (err) {
      setAlert({
        show: true,
        message: err.response?.data?.message || 'Error updating student.',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!formData)
    return (
      <Layout>
        <div
          style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            padding: '30px 20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <Spinner animation="border" variant="primary" />
            <p style={{ marginTop: '15px', color: '#6c757d' }}>
              Loading student details...
            </p>
          </div>
        </div>
      </Layout>
    );

  return (
    <Layout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          padding: '30px 20px',
        }}
      >
        {/* Header Card */}
        <div
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            color: 'white',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            <div>
              <h4
                style={{
                  margin: 0,
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <FaUserEdit style={{ fontSize: '1.1rem' }} />
                Edit Student
                {formData?.name && (
                  <span
                    style={{
                      fontSize: '0.9rem',
                      fontWeight: '400',
                      marginLeft: '8px',
                      opacity: 0.9,
                    }}
                  >
                    - {formData.name}
                  </span>
                )}
                {formData?.admission_no && (
                  <Badge
                    bg="light"
                    text="dark"
                    style={{ marginLeft: '8px', fontSize: '0.75rem' }}
                  >
                    {formData.admission_no}
                  </Badge>
                )}
              </h4>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Button
                variant="light"
                onClick={handleSubmit}
                disabled={loading}
                style={{
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontWeight: '500',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                <FaSave size={14} /> {loading ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                variant="outline-light"
                onClick={() => navigate(-1)}
                style={{
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontWeight: '500',
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                <FaArrowLeft size={14} /> Back
              </Button>
            </div>
          </div>
        </div>

        {/* Alert Messages */}
        {alert.show && (
          <Alert
            variant={alert.variant}
            dismissible
            onClose={() => setAlert({ ...alert, show: false })}
            style={{
              borderRadius: '8px',
              marginBottom: '16px',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Form Card */}
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spinner animation="border" variant="primary" />
              <p style={{ marginTop: '15px', color: '#6c757d' }}>
                Updating student...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="row g-4">
                {/* LEFT COLUMN */}
                <div className="col-12 col-lg-6">
                  <h5
                    style={{
                      color: '#667eea',
                      marginBottom: '20px',
                      fontWeight: '600',
                    }}
                  >
                    Personal Information
                  </h5>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Full Name <span style={{ color: '#dc3545' }}>*</span>
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          backgroundColor: 'white',
                          borderRight: 'none',
                          borderRadius: '8px 0 0 8px',
                        }}
                      >
                        <FaUser style={{ color: '#6c757d' }} />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name || ''}
                        onChange={handleChange}
                        required
                        placeholder="Enter full name"
                        style={{
                          borderLeft: 'none',
                          borderRadius: '0 8px 8px 0',
                          paddingLeft: '0',
                        }}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Gender <span style={{ color: '#dc3545' }}>*</span>
                    </Form.Label>
                    <Form.Select
                      name="gender"
                      value={formData.gender || ''}
                      onChange={handleChange}
                      style={{ borderRadius: '8px' }}
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Date of Birth
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          backgroundColor: 'white',
                          borderRight: 'none',
                          borderRadius: '8px 0 0 8px',
                        }}
                      >
                        <FaCalendar style={{ color: '#6c757d' }} />
                      </InputGroup.Text>
                      <Form.Control
                        type="date"
                        name="dob"
                        value={
                          formData.dob !== '0000-00-00' ? formData.dob : ''
                        }
                        onChange={handleChange}
                        style={{
                          borderLeft: 'none',
                          borderRadius: '0 8px 8px 0',
                          paddingLeft: '0',
                        }}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Parents/Guardian
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          backgroundColor: 'white',
                          borderRight: 'none',
                          borderRadius: '8px 0 0 8px',
                        }}
                      >
                        <FaUsers style={{ color: '#6c757d' }} />
                      </InputGroup.Text>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="parents"
                        value={formData.parents || ''}
                        onChange={handleChange}
                        placeholder="Parent or guardian name"
                        style={{
                          borderLeft: 'none',
                          borderRadius: '0 8px 8px 0',
                          paddingLeft: '0',
                        }}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Mobile Number
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          backgroundColor: 'white',
                          borderRight: 'none',
                          borderRadius: '8px 0 0 8px',
                        }}
                      >
                        <FaPhone style={{ color: '#6c757d' }} />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="mobile"
                        value={formData.mobile || ''}
                        onChange={handleChange}
                        placeholder="Contact number(s)"
                        style={{
                          borderLeft: 'none',
                          borderRadius: '0 8px 8px 0',
                          paddingLeft: '0',
                        }}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Address
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          backgroundColor: 'white',
                          borderRight: 'none',
                          borderRadius: '8px 0 0 8px',
                          alignItems: 'flex-start',
                          paddingTop: '10px',
                        }}
                      >
                        <FaMapMarkerAlt style={{ color: '#6c757d' }} />
                      </InputGroup.Text>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="address"
                        value={formData.address || ''}
                        onChange={handleChange}
                        placeholder="Full address"
                        style={{
                          borderLeft: 'none',
                          borderRadius: '0 8px 8px 0',
                          paddingLeft: '0',
                        }}
                      />
                    </InputGroup>
                  </Form.Group>
                </div>

                {/* RIGHT COLUMN */}
                <div className="col-12 col-lg-6">
                  <h5
                    style={{
                      color: '#667eea',
                      marginBottom: '20px',
                      fontWeight: '600',
                    }}
                  >
                    Academic & Administrative
                  </h5>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Date of Application
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          backgroundColor: 'white',
                          borderRight: 'none',
                          borderRadius: '8px 0 0 8px',
                        }}
                      >
                        <FaCalendar style={{ color: '#6c757d' }} />
                      </InputGroup.Text>
                      <Form.Control
                        type="date"
                        name="date_of_application"
                        value={formData.date_of_application}
                        onChange={handleChange}
                        style={{
                          borderLeft: 'none',
                          borderRadius: '0 8px 8px 0',
                          paddingLeft: '0',
                        }}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      ID Card Number
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          backgroundColor: 'white',
                          borderRight: 'none',
                          borderRadius: '8px 0 0 8px',
                        }}
                      >
                        <FaIdCard style={{ color: '#6c757d' }} />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        name="id_card_no"
                        value={formData.id_card_no || ''}
                        onChange={handleChange}
                        placeholder="ID card number"
                        style={{
                          borderLeft: 'none',
                          borderRadius: '0 8px 8px 0',
                          paddingLeft: '0',
                        }}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Batches
                    </Form.Label>
                    <Select
                      isMulti
                      options={batches.map((batch) => ({
                        value: String(batch.id),
                        label: `${batch.class_name} ${batch.division}`,
                      }))}
                      value={batches
                        .filter((b) => selectedBatches.includes(String(b.id)))
                        .map((b) => ({
                          value: String(b.id),
                          label: `${b.class_name} ${b.division}`,
                        }))}
                      onChange={(selectedOptions) =>
                        setSelectedBatches(
                          selectedOptions.map((opt) => opt.value),
                        )
                      }
                      classNamePrefix="react-select"
                      placeholder="Select one or more batches..."
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: '8px',
                          borderColor: '#dee2e6',
                        }),
                      }}
                    />
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Upload Photo
                    </Form.Label>
                    <div
                      style={{
                        border: '2px dashed #dee2e6',
                        borderRadius: '8px',
                        padding: '20px',
                        textAlign: 'center',
                        backgroundColor: '#f8f9fa',
                      }}
                    >
                      {photoPreview ? (
                        <div>
                          <img
                            src={photoPreview}
                            alt="Preview"
                            style={{
                              width: '150px',
                              height: '150px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              marginBottom: '15px',
                              border: '2px solid #667eea',
                            }}
                          />
                          <div>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => {
                                setPhoto(null);
                                setPhotoPreview(null);
                                setFormData({ ...formData, photo: '' });
                                if (fileInputRef.current)
                                  fileInputRef.current.value = '';
                              }}
                              style={{ borderRadius: '6px' }}
                            >
                              Remove Photo
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <FaCamera
                            style={{
                              fontSize: '3rem',
                              color: '#6c757d',
                              marginBottom: '10px',
                            }}
                          />
                          <p style={{ margin: '10px 0 0 0', color: '#6c757d' }}>
                            Click to upload photo
                          </p>
                        </div>
                      )}
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        onChange={handlePhotoChange}
                        style={{ display: 'none' }}
                      />
                      {!photoPreview && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          style={{ marginTop: '10px', borderRadius: '6px' }}
                        >
                          Choose File
                        </Button>
                      )}
                    </div>
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Remark
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text
                        style={{
                          backgroundColor: 'white',
                          borderRight: 'none',
                          borderRadius: '8px 0 0 8px',
                          alignItems: 'flex-start',
                          paddingTop: '10px',
                        }}
                      >
                        <FaStickyNote style={{ color: '#6c757d' }} />
                      </InputGroup.Text>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="remark"
                        value={formData.remark || ''}
                        onChange={handleChange}
                        placeholder="Additional notes"
                        style={{
                          borderLeft: 'none',
                          borderRadius: '0 8px 8px 0',
                          paddingLeft: '0',
                        }}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '20px' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Status
                    </Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status || 'Active'}
                      onChange={handleChange}
                      style={{ borderRadius: '8px' }}
                    >
                      <option value="Active">Active</option>
                      <option value="Suspend">Suspend</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group style={{ marginBottom: '0' }}>
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Parent Access Code
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="parent_access_code"
                      value={formData.parent_access_code}
                      readOnly
                      style={{
                        borderRadius: '8px',
                        backgroundColor: '#f8f9fa',
                      }}
                    />
                    <Form.Text className="text-muted">
                      Auto-generated code for parent access
                    </Form.Text>
                  </Form.Group>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default StudentEdit;
